// ═════════════════════════════════════════════════════════════════════════════
//  LO QUE EL COACH ENSEÑA MIENTRAS HABLA
//
//  Rob: «lo que le va a dar el impacto visual, que se muestra para apoyar al
//  coach: las preguntas del alumno, calificaciones, etc.»
//
//  La pantalla del coach ya llevaba escrito el reparto —«la voz lleva la
//  personalidad, la pantalla lleva los datos»— pero solo se cumplia a medias:
//  los asistentes sacaban un 72, cuatro destrezas y seis temas fallados, y todo
//  eso se le daba UNICAMENTE al modelo, que lo resumia en dos frases y tiraba
//  el resto. El alumno oia «vas flojo en listening» y no veia nunca el numero.
//
//  ── CUATRO PIEZAS, NO UNA TARJETA POR ASISTENTE ─────────────────────────────
//  El backend no manda "una tarjeta de examen" ni "una tarjeta de destrezas":
//  manda BLOQUES —anillo, barras, fichas, etiquetas— y este componente sabe
//  pintar esos cuatro. Anadir un asistente con tarjeta no toca este fichero.
//
//  ── DE DONDE SALEN LOS NUMEROS ──────────────────────────────────────────────
//  Del asistente, nunca del modelo. Es la misma ley que ya rige los enlaces de
//  clase y las palabras del juego. Una cifra en pantalla se cree mucho mas que
//  una frase: un coach que se equivoca hablando es un despistado, uno que se
//  equivoca en un marcador es un mentiroso.
// ═════════════════════════════════════════════════════════════════════════════

import "./CoachTarjeta.css";

// El aro se dibuja con SVG y no con un `conic-gradient` de CSS. El degradado
// conico no se puede animar sin `@property`, que Firefox solo trae desde la
// 128; un `stroke-dashoffset` lo anima cualquier navegador desde hace diez
// anios. Esto corre en el telefono de un alumno, no en un banco de pruebas.
function Anillo({ valor, centro, pie }) {
  const RADIO = 42;
  const VUELTA = 2 * Math.PI * RADIO;
  const relleno = VUELTA * (Math.max(0, Math.min(100, valor)) / 100);

  return (
    <div className="cct-anillo">
      <svg viewBox="0 0 100 100" className="cct-aro" aria-hidden="true">
        <circle className="cct-aro-fondo" cx="50" cy="50" r={RADIO} />
        <circle
          className="cct-aro-valor"
          cx="50" cy="50" r={RADIO}
          strokeDasharray={`${relleno} ${VUELTA}`}
        />
      </svg>
      <div className="cct-aro-centro">
        <span className="cct-aro-cifra">{centro}</span>
        {pie && <span className="cct-aro-pie">{pie}</span>}
      </div>
    </div>
  );
}

function Barras({ items }) {
  return (
    <ul className="cct-barras">
      {items.map((b, i) => (
        <li className="cct-barra" key={i}>
          <span className="cct-barra-eti">{b.etiqueta}</span>
          {/* El carril va hundido y el relleno levantado, como los medidores de
              las referencias: el dato parece estar DENTRO de la pantalla. */}
          <span className="cct-barra-carril">
            <span className="cct-barra-relleno" style={{ width: `${b.valor}%` }} />
          </span>
          <span className="cct-barra-num">{b.nota || `${b.valor}`}</span>
        </li>
      ))}
    </ul>
  );
}

function Fichas({ items }) {
  return (
    <div className="cct-fichas">
      {items.map((f, i) => (
        <div className="cct-ficha" key={i}>
          <span className="cct-ficha-cifra">{f.cifra}</span>
          <span className="cct-ficha-eti">{f.etiqueta}</span>
        </div>
      ))}
    </div>
  );
}

function Etiquetas({ titulo, items }) {
  return (
    <div className="cct-etiquetas">
      {titulo && <p className="cct-etiquetas-tit">{titulo}</p>}
      <div className="cct-etiquetas-lista">
        {items.map((e, i) => (
          <span className={`cct-etiqueta cct-tono-${e.tono || "falla"}`} key={i}>
            {e.texto}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function CoachTarjeta({ tarjeta }) {
  if (!tarjeta || !Array.isArray(tarjeta.bloques) || tarjeta.bloques.length === 0) {
    return null;
  }

  return (
    <section className="cct-tarjeta" aria-label={tarjeta.titulo}>
      <header className="cct-cabecera">
        <span className="cct-icono" aria-hidden="true">{tarjeta.icono}</span>
        <h3 className="cct-titulo">{tarjeta.titulo}</h3>
      </header>

      <div className="cct-cuerpo">
        {tarjeta.bloques.map((b, i) => {
          if (b.tipo === "anillo") {
            return <Anillo key={i} valor={b.valor} centro={b.centro} pie={b.pie} />;
          }
          if (b.tipo === "barras") return <Barras key={i} items={b.items || []} />;
          if (b.tipo === "fichas") return <Fichas key={i} items={b.items || []} />;
          if (b.tipo === "etiquetas") {
            return <Etiquetas key={i} titulo={b.titulo} items={b.items || []} />;
          }
          // Un bloque que esta pantalla no conoce se ignora en silencio. Asi el
          // backend puede estrenar una pieza nueva sin romper a quien todavia
          // no la sabe pintar.
          return null;
        })}
      </div>
    </section>
  );
}
